import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSocket } from '../../contexts/SocketContext'
import { AuthContext } from '../../contexts/AuthContext'
import { toast } from '../shared/NotificationToast'
import { ChatMessage as MessageComponent } from '../chat/ChatMessage'
import type { ChatMessage as UIChatMessage } from '../../hooks/useChatThread'
import { formatDate } from '../../utils/format'
import {
  getTicket,
  claimTicket,
  setTicketStatus,
  replyTicket,
  agentUploadFile,
  getMySignature,
  getErrorMessage,
  AGENT_UPLOAD_MAX_BYTES,
  type TicketDetail,
  type TicketMessage,
  type TicketStatus,
  type UploadedFile,
} from '../../utils/api'
import { TicketStatusBadge, TicketChannelBadge } from './TicketBadges'
import { RichTextEditor, type RichTextEditorHandle } from '../shared/RichTextEditor'
import { useForwarding } from '../../contexts/ForwardingContext'
import { sameId } from '../../contexts/forwarding'

/** Escape plain text and wrap it as simple HTML paragraphs for the editor. */
function plainToHtml(text: string): string {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return text
    .split(/\n{2,}/)
    .map((para) => `<p>${esc(para).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

function fmtDateTime(s?: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
}

/** Map an API ticket message to the shape the ChatMessage component renders. */
function mapMessage(m: TicketMessage): UIChatMessage {
  const isSystem = m.message_type === 'system' || m.sender_type === 'system'
  return {
    id: String(m.id),
    chatId: '',
    sender: isSystem ? 'system' : m.sender_type === 'agent' ? 'agent' : 'customer',
    senderName: isSystem ? '' : m.sender_type === 'agent' ? 'You' : 'Customer',
    content: m.body || '',
    attachments: (m.attachments || []).map((a, i) => ({
      id: `${m.id}-${i}`,
      url: a.url,
      filename: a.name || 'file',
      type: (a.type as 'image' | 'video' | 'file') || 'file',
      size: a.size || 0,
    })),
    isInternal: false,
    createdAt: m.created_at,
    // Carry the message_type so ChatMessage can render rich HTML agent replies
    // (message_type === 'html') as sanitized HTML; everything else stays plain.
    messageType: m.message_type,
  }
}

interface TicketConversationProps {
  ticketId: string | null
  /** Optional: shown on narrow screens to return to the list. */
  onBack?: () => void
}

export const TicketConversation: React.FC<TicketConversationProps> = ({ ticketId, onBack }) => {
  const { socket } = useSocket()
  const auth = useContext(AuthContext)
  const currentAgentId = auth?.currentAgent?.id
  const { consume } = useForwarding()

  const [ticket, setTicket] = useState<TicketDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [claiming, setClaiming] = useState(false)
  const [savingStatus, setSavingStatus] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // Reply composer state. `replyHtml` is the editor's HTML; the editor ref
  // exposes the plain-text version for the required `text` field on send.
  const [replyHtml, setReplyHtml] = useState('')
  const [sending, setSending] = useState(false)
  const editorRef = useRef<RichTextEditorHandle>(null)

  // Attachments uploaded for this reply (cleared on send / ticket switch).
  const [attachments, setAttachments] = useState<UploadedFile[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Whether the agent has a saved signature (for the "signature added" hint).
  const [hasSignature, setHasSignature] = useState(false)

  const idRef = useRef(ticketId)
  idRef.current = ticketId
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    if (!idRef.current) return
    try {
      const t = await getTicket(idRef.current)
      setTicket(t)
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load ticket'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!ticketId) {
      setTicket(null)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setReplyHtml('')
    setAttachments([])
    load()
  }, [ticketId, load])

  // Claim a forwarded Copilot answer destined for THIS ticket: prefill the
  // reply editor (as simple HTML) + focus, never auto-send. consume() returns
  // null on a direct load or when the forward targets a different ticket.
  useEffect(() => {
    if (!ticketId) return
    const text = consume((t) => t.kind === 'ticket' && sameId(t.id, ticketId))
    if (text === null) return
    const html = plainToHtml(text)
    setReplyHtml(html)
    // The editor syncs to `replyHtml` once mounted; focus after a tick.
    const id = window.setTimeout(() => editorRef.current?.focus(), 60)
    return () => window.clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId])

  // Load whether the agent has a signature once, to show the composer hint.
  useEffect(() => {
    let active = true
    getMySignature()
      .then((res) => {
        if (active) setHasSignature(!!(res.signature && res.signature.trim()))
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [])

  // Opening the ticket marks it read locally — the detail fetch is the
  // mark-as-read signal; we just clear the badge in our state so it doesn't
  // linger while the list refetches.
  useEffect(() => {
    if (ticket?.unread) {
      setTicket((prev) => (prev ? { ...prev, unread: false } : prev))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id])

  // Append a new message to the open ticket, de-duplicating by id.
  const appendMessage = useCallback((raw: any) => {
    const msg: TicketMessage = {
      id: raw.id,
      sender_type:
        raw.sender_type === 'agent' ? 'agent' : raw.sender_type === 'system' ? 'system' : 'customer',
      sender_id: raw.sender_id ?? null,
      body: raw.body || raw.content || '',
      message_type: raw.message_type,
      created_at: raw.created_at || raw.createdAt || new Date().toISOString(),
      attachments: (raw.attachments || []).map((a: any) => ({
        url: a.url,
        name: a.name || a.filename || 'file',
        type: a.type || 'file',
        size: a.size || 0,
      })),
    }
    setTicket((prev) => {
      if (!prev) return prev
      if (prev.messages.some((m) => String(m.id) === String(msg.id))) return prev
      return { ...prev, messages: [...prev.messages, msg] }
    })
  }, [])

  // Live updates for this ticket: ticket_updated (refetch header fields) plus
  // new_message / message_received scoped to this ticket's chat.
  useEffect(() => {
    if (!socket || !ticketId) return

    const onTicketUpdated = (payload: any) => {
      const pid = String(payload?.id ?? '')
      if (pid && pid === String(idRef.current)) load()
    }

    const chatId = ticket?.chat_id != null ? String(ticket.chat_id) : null

    const onNewMessage = (payload: any) => {
      // `new_message` arrives as { chatId, message }; `message_received` is the
      // raw message. Match on the ticket's backing chat id when available.
      const msgChatId = String(payload?.chatId ?? payload?.chat_id ?? '')
      const message = payload?.message ?? payload
      if (chatId && msgChatId && msgChatId !== chatId) return
      if (!message) return
      appendMessage(message)
    }

    socket.on('ticket_updated', onTicketUpdated)
    socket.on('new_message', onNewMessage)
    if (chatId) socket.on('message_received', onNewMessage)

    return () => {
      socket.off('ticket_updated', onTicketUpdated)
      socket.off('new_message', onNewMessage)
      if (chatId) socket.off('message_received', onNewMessage)
    }
  }, [socket, load, ticketId, ticket?.chat_id, appendMessage])

  // Auto-scroll the thread to the bottom on new messages.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [ticket?.messages])

  const handleClaim = async () => {
    if (!ticket) return
    setActionError(null)
    setClaiming(true)
    try {
      const updated = await claimTicket(ticket.id)
      setTicket((prev) => (prev ? { ...prev, ...updated } : prev))
      toast.success('Ticket claimed')
    } catch (err) {
      const status = (err as { status?: number })?.status
      if (status === 409) {
        toast.error('Already claimed', 'This ticket was claimed by someone else.')
      } else {
        toast.error('Claim failed', getErrorMessage(err, 'Could not claim ticket'))
      }
      load()
    } finally {
      setClaiming(false)
    }
  }

  const handleStatus = async (status: TicketStatus) => {
    if (!ticket || ticket.status === status) return
    setActionError(null)
    setSavingStatus(true)
    try {
      const updated = await setTicketStatus(ticket.id, status)
      setTicket((prev) => (prev ? { ...prev, ...updated } : prev))
      toast.success(`Status set to ${status}`)
    } catch (err) {
      const msg = getErrorMessage(err, 'Could not update status')
      setActionError(msg)
      toast.error('Update failed', msg)
    } finally {
      setSavingStatus(false)
    }
  }

  // ── Attachments ──
  const onPickFiles = () => fileInputRef.current?.click()

  const onFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    e.target.value = '' // allow re-selecting the same file
    if (files.length === 0) return

    setUploading(true)
    try {
      for (const file of files) {
        if (file.size > AGENT_UPLOAD_MAX_BYTES) {
          toast.error(
            'File too large',
            `${file.name} exceeds the ${Math.round(AGENT_UPLOAD_MAX_BYTES / (1024 * 1024))}MB limit.`
          )
          continue
        }
        try {
          const uploaded = await agentUploadFile(file)
          setAttachments((prev) => [...prev, uploaded])
        } catch (err) {
          toast.error('Upload failed', getErrorMessage(err, `Could not upload ${file.name}`))
        }
      }
    } finally {
      setUploading(false)
    }
  }

  const removeAttachment = (id: UploadedFile['id']) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  // `replyHtml` updates on every edit, so the Send button reacts to typing.
  const hasBody = replyHtml.trim().length > 0
  const canSend = !sending && !uploading && (hasBody || attachments.length > 0)

  const handleSendReply = async () => {
    if (!ticket || sending || uploading) return
    const text = (editorRef.current?.getText() || '').trim()
    const html = editorRef.current?.getHTML() || ''
    // `text` is required unless there's an attachment.
    if (!text && attachments.length === 0) return

    setActionError(null)
    setSending(true)
    try {
      const res = await replyTicket(ticket.id, {
        // The server appends the signature to both email + stored message, so
        // we send ONLY the body here — never the signature — to avoid dupes.
        text: text || ' ',
        html: html || undefined,
        attachments: attachments.length
          ? attachments.map((a) => ({ url: a.url, name: a.name, type: a.type, size: a.size }))
          : undefined,
      })
      setTicket((prev) => {
        if (!prev) return prev
        const merged = { ...prev, ...res.ticket }
        const exists = prev.messages.some((m) => String(m.id) === String(res.message.id))
        return {
          ...merged,
          messages: exists ? prev.messages : [...prev.messages, res.message],
        }
      })
      setReplyHtml('')
      setAttachments([])
      toast.success('Reply sent')
    } catch (err) {
      const msg = getErrorMessage(err, 'Could not send reply')
      setActionError(msg)
      toast.error('Reply failed', msg)
    } finally {
      setSending(false)
    }
  }

  const fmtSize = (bytes: number) =>
    bytes > 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`

  // Group mapped messages by day for date dividers.
  const groupedMessages = useMemo(() => {
    const mapped = (ticket?.messages || []).map(mapMessage)
    return mapped.reduce(
      (acc, msg) => {
        const date = formatDate(msg.createdAt)
        if (!acc[date]) acc[date] = []
        acc[date].push(msg)
        return acc
      },
      {} as Record<string, UIChatMessage[]>
    )
  }, [ticket?.messages])

  // ── Empty state: nothing selected ──
  if (!ticketId) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-8 bg-surface-secondary/30">
        <svg
          className="w-14 h-14 text-txt-tertiary mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
          />
        </svg>
        <p className="text-sm font-medium text-txt-secondary">
          Select a ticket to view the conversation
        </p>
        <p className="text-xs text-txt-tertiary mt-1">
          Every ticket is an email thread you can reply to from here.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-sm text-txt-secondary">Loading ticket...</span>
      </div>
    )
  }

  if (error || !ticket) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto">
        {onBack && (
          <button onClick={onBack} className="text-sm text-brand-600 hover:underline mb-4">
            ← Back to tickets
          </button>
        )}
        <div className="px-4 py-3 rounded-lg bg-error-light text-error text-sm">
          {error || 'Ticket not found'}
        </div>
      </div>
    )
  }

  const isUnassigned = ticket.assigned_agent_id == null || ticket.assigned_agent_id === ''
  const assignedToMe =
    currentAgentId != null && String(ticket.assigned_agent_id) === String(currentAgentId)

  const statusBtn = (s: TicketStatus, label: string) => (
    <button
      key={s}
      onClick={() => handleStatus(s)}
      disabled={savingStatus || ticket.status === s}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors disabled:cursor-not-allowed ${
        ticket.status === s
          ? 'bg-brand-600 text-white border-brand-600'
          : 'bg-surface-secondary hover:bg-surface-hover text-txt-primary border-border disabled:opacity-50'
      }`}
    >
      {label}
    </button>
  )

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 bg-surface border-b border-border">
        {onBack && (
          <button
            onClick={onBack}
            className="text-sm text-brand-600 hover:underline mb-3 inline-flex items-center gap-1 md:hidden"
          >
            ← Back to tickets
          </button>
        )}

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-lg font-bold text-txt-primary font-mono">
                {ticket.ticket_number}
              </h1>
              <TicketChannelBadge value={ticket.channel} />
              <TicketStatusBadge value={ticket.status} />
            </div>
            <p className="text-sm text-txt-primary font-medium mt-1.5 truncate max-w-2xl">
              {ticket.subject || 'No subject'}
            </p>
            <p className="text-xs text-txt-tertiary mt-0.5">
              {ticket.visitor_name || 'Anonymous'}
              {ticket.visitor_email ? ` · ${ticket.visitor_email}` : ''}
            </p>
          </div>

          {/* Assignee + claim */}
          <div className="flex items-center gap-2 shrink-0">
            {isUnassigned ? (
              <button
                onClick={handleClaim}
                disabled={claiming}
                className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium shadow-sm disabled:opacity-50 transition-colors"
              >
                {claiming ? 'Claiming…' : 'Claim'}
              </button>
            ) : (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-50 dark:bg-brand-900/30 border border-brand-200 dark:border-brand-800 text-xs font-medium text-brand-700 dark:text-brand-300"
                title={`Assigned to ${ticket.assigned_agent_name || ticket.assigned_agent_id}`}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                {assignedToMe ? 'You' : ticket.assigned_agent_name || `Agent ${ticket.assigned_agent_id}`}
              </span>
            )}
          </div>
        </div>

        {/* Status controls */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <span className="text-xs font-semibold uppercase tracking-wider text-txt-tertiary mr-1">
            Status
          </span>
          {statusBtn('open', 'Open')}
          {statusBtn('answered', 'Answered')}
          {statusBtn('closed', 'Closed')}
        </div>

        {actionError && (
          <div className="mt-3 px-4 py-2.5 rounded-lg bg-error-light text-error text-sm">
            {actionError}
          </div>
        )}
      </div>

      {/* Email thread */}
      <div className="flex-1 min-h-0 overflow-y-auto py-4">
        {ticket.messages.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-sm text-txt-tertiary">No messages in this thread yet.</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              <div className="flex items-center gap-4 my-4 px-5">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[10px] font-semibold text-txt-tertiary uppercase tracking-wider">
                  {date}
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>
              {dateMessages.map((msg, idx) => (
                <MessageComponent
                  key={msg.id}
                  message={msg}
                  prevMessage={idx > 0 ? dateMessages[idx - 1] : undefined}
                  forwardSource={{
                    kind: 'ticket',
                    id: ticket.id,
                    label:
                      ticket.visitor_name ||
                      ticket.ticket_number ||
                      'this ticket',
                    path: `/tickets/${ticket.id}`,
                  }}
                />
              ))}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply composer */}
      <div className="flex-shrink-0 border-t border-border bg-surface p-4">
        {ticket.status === 'closed' && (
          <p className="text-xs text-txt-tertiary mb-2">
            This ticket is closed. Sending a reply will keep its status; set it to Open or Answered
            from the controls above if needed.
          </p>
        )}
        <RichTextEditor
          ref={editorRef}
          value={replyHtml}
          onChange={setReplyHtml}
          onSubmit={handleSendReply}
          disabled={sending}
          placeholder="Type your email reply to the customer…"
          ariaLabel="Email reply to the customer"
        />

        {/* Attachment chips */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {attachments.map((a) => (
              <span
                key={a.id}
                className="inline-flex items-center gap-1.5 max-w-[14rem] pl-2 pr-1 py-1 rounded-md bg-surface-secondary border border-border text-xs text-txt-secondary"
                title={a.name}
              >
                <svg className="w-3.5 h-3.5 shrink-0 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                </svg>
                <span className="truncate">{a.name}</span>
                <span className="shrink-0 text-txt-tertiary">· {fmtSize(a.size)}</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(a.id)}
                  disabled={sending}
                  aria-label={`Remove ${a.name}`}
                  className="shrink-0 w-5 h-5 inline-flex items-center justify-center rounded text-txt-tertiary hover:text-error hover:bg-error-light/50 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Composer action bar */}
        <div className="flex items-center justify-between gap-3 mt-2">
          <div className="flex items-center gap-2 min-w-0">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={onFilesSelected}
            />
            <button
              type="button"
              onClick={onPickFiles}
              disabled={sending || uploading}
              aria-label="Attach files"
              title="Attach files"
              className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-txt-secondary hover:bg-surface-hover hover:text-txt-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? (
                <svg className="w-4 h-4 animate-spin-slow" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                </svg>
              )}
            </button>
            <span className="text-[10px] text-txt-tertiary hidden sm:inline truncate">
              {uploading
                ? 'Uploading…'
                : hasSignature
                  ? 'Cmd/Ctrl + Enter to send · your signature will be added'
                  : 'Cmd/Ctrl + Enter to send · the customer is emailed in-thread'}
            </span>
          </div>
          <button
            onClick={handleSendReply}
            disabled={!canSend}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            {sending ? (
              <>
                <svg className="w-3.5 h-3.5 animate-spin-slow" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending
              </>
            ) : (
              <>
                Send reply
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
              </>
            )}
          </button>
        </div>
        {/* Optional metadata footer */}
        <p className="text-[10px] text-txt-tertiary mt-2">
          Created {fmtDateTime(ticket.created_at)}
          {ticket.gmail_thread_id ? ` · thread ${ticket.gmail_thread_id}` : ''}
        </p>
      </div>
    </div>
  )
}
